import { useAppDispatch } from "app/hooks";
import FormType from "enums/FormType";
import ModuleStatus from "enums/ModuleStatus";
import FormEditorContainer from "features/qualityForm/formEditor/FormEditorContainer";
import FormViewerPreview from "features/qualityForm/formEditor/FormViewerPreview";
import {
    loadForm,
    sendAssessmentFormData,
} from "features/qualityForm/qualityFormSlice";
import {
    getPreviewScores,
    previewLoadScorable,
} from "features/scorable/scorableSlice";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import embeddingService from "services/embeddingService";
import windowEventService from "services/windowEventService";
import { NotFound } from "./NotFound";

interface IAssessmentFormProps {
    preview?: boolean;
}

type AssessmentFormParams = {
    moduleStatus: ModuleStatus;
};

function AssessmentFormEditor({ preview }: { preview?: boolean }) {
    const dispatch = useAppDispatch();

    const onAssessmentFormDataReceived = useCallback(
        (event: MessageEvent) => {
            const messageData = event.data;
            if (
                event.source === window.parent &&
                messageData.type == "set-assessment-form-data"
            ) {
                dispatch(
                    loadForm({
                        formType: FormType.ASSESSMENT,
                        formId: "new",
                        assessmentForm: messageData.assessmentForm,
                        isArchived: messageData.isArchived,
                    })
                ).then((e) => {
                    if ("error" in e) {
                        embeddingService.notifyErrors(
                            "assessment-loading-failed"
                        );
                    } else {
                        embeddingService.sendFormLoadedStatus(true);
                        if (preview) {
                            dispatch(previewLoadScorable());
                            dispatch(
                                getPreviewScores({
                                    isScorableFinished: false,
                                    isInitialLoad: true,
                                })
                            );
                        }
                    }
                });
            }
        },
        [dispatch]
    );

    const saveAssessmentFormListener = useCallback(
        (event: MessageEvent) => {
            const messageData = event.data;
            if (
                event.source === window.parent &&
                messageData.type == "request-form-data"
            ) {
                dispatch(sendAssessmentFormData());
            }
        },
        [dispatch]
    );

    useEffect(() => {
        windowEventService.registerEvent(
            "assessment-form-data-received-event-listener",
            "message",
            onAssessmentFormDataReceived
        );

        embeddingService.sendFormReadyStatus(true);

        return () => {
            windowEventService.removeEvent(
                "assessment-form-data-received-event-listener",
                "message"
            );
        };
    }, [onAssessmentFormDataReceived]);

    useEffect(() => {
        windowEventService.registerEvent(
            "qm-form-editor-message",
            "message",
            saveAssessmentFormListener
        );

        return () => {
            windowEventService.removeEvent("qm-form-editor-message", "message");
        };
    }, [saveAssessmentFormListener]);
    return preview ? (
        <FormViewerPreview />
    ) : (
        <FormEditorContainer formType={FormType.ASSESSMENT} />
    );
}

export function AssessmentForm({ preview }: IAssessmentFormProps) {
    const { moduleStatus } = useParams<AssessmentFormParams>();

    // add role main since assessment form preview is not embedded in web-directory
    if (preview) {
        document
            .querySelector(".quality-forms-app")
            ?.setAttribute("role", "main");
    }

    const isValidModuleStatus = Object.values(ModuleStatus).some(
        (status) => status === moduleStatus
    );

    return isValidModuleStatus ? (
        <AssessmentFormEditor preview={preview} />
    ) : (
        <NotFound />
    );
}
